<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { RouterView } from 'vue-router'
import OfflineBanner from '@shared/ui/OfflineBanner.vue'
import UpdatePrompt from '@shared/ui/UpdatePrompt.vue'
import CommandPalette from '@shared/ui/CommandPalette.vue'
import SidebarNav from '@shared/ui/SidebarNav.vue'
import ErrorBoundary from '@shared/ui/ErrorBoundary.vue'
import ToastContainer from '@shared/ui/ToastContainer.vue'
import { useOfflineSync } from '@shared/composables/useOfflineSync'

useOfflineSync()

const route = useRoute()
const mobileMenuOpen = ref(false)
const isEmbed = computed(() => route.query.embed === '1')

const navTabs = [
  { path: '/todo', label: '待办', emoji: '📋' },
  { path: '/schedule', label: '日程', emoji: '🗓️' },
  { path: '/notifications', label: '通知', emoji: '🔔' },
  { path: '/tools', label: '工具', emoji: '🛠' }
]

function isActive(path: string): boolean {
  return route.path.startsWith(path)
}

function closeMobileMenu() {
  mobileMenuOpen.value = false
}
</script>

<template>
  <OfflineBanner />

  <!-- Login page: centered, no sidebar -->
  <div v-if="route.name === 'login'" class="min-h-screen flex items-center justify-center p-md">
    <ErrorBoundary>
      <RouterView />
    </ErrorBoundary>
  </div>

  <!-- Normal layout -->
  <div v-else class="flex min-h-screen h-screen overflow-hidden">
    <!-- Mobile sidebar overlay -->
    <div
      v-if="mobileMenuOpen && !isEmbed"
      class="fixed inset-0 z-50 lg:hidden"
      @click="closeMobileMenu"
    >
      <div class="absolute inset-0 bg-black/50" />
      <aside class="absolute inset-y-0 left-0 w-64 bg-background-primary shadow-lg">
        <SidebarNav />
      </aside>
    </div>

    <!-- Desktop sidebar -->
    <aside v-if="!isEmbed" class="hidden lg:block sticky top-0 self-start h-screen flex-shrink-0 overflow-y-auto">
      <SidebarNav />
    </aside>

    <!-- Main content -->
    <main class="flex-1 p-md pb-20 lg:pb-md overflow-y-auto">
      <header v-if="!isEmbed" class="max-w-2xl lg:max-w-3xl mx-auto flex items-center justify-between mb-md">
        <button
          class="lg:hidden text-text-primary text-xl mr-sm"
          @click="mobileMenuOpen = true"
        >☰</button>
        <h1 class="text-text-primary text-lg font-bold">不系舟</h1>
        <CommandPalette />
      </header>
      <ErrorBoundary>
        <RouterView v-slot="{ Component }">
          <Transition name="page" mode="out-in">
            <component :is="Component" :key="route.path" />
          </Transition>
        </RouterView>
      </ErrorBoundary>
    </main>
  </div>

  <!-- Mobile bottom tab bar -->
  <nav
    v-if="route.name !== 'login' && !isEmbed"
    class="fixed bottom-0 inset-x-0 lg:hidden z-40 border-t"
    :style="{
      background: 'var(--surface-glass)',
      borderColor: 'var(--surface-glass-border)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      paddingBottom: 'env(safe-area-inset-bottom, 0px)'
    }"
  >
    <div class="flex justify-around">
      <RouterLink
        v-for="tab in navTabs"
        :key="tab.path"
        :to="tab.path"
        class="flex flex-col items-center py-sm px-xs text-xs"
        :class="isActive(tab.path) ? 'text-brand-primary' : 'text-text-secondary'"
      >
        <span class="text-lg">{{ tab.emoji }}</span>
        <span>{{ tab.label }}</span>
      </RouterLink>
    </div>
  </nav>

  <UpdatePrompt />
  <ToastContainer />
</template>
